import { useState, useCallback, useEffect } from 'react';
import { formatPhone } from '../../../../helpers/phoneUtils';
import {
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    Grid,
    CircularProgress,
    TableContainer,
    TextField,
    MenuItem
} from '@material-ui/core';
import AgentListUploader from './AgentListUploader';
import ActionIconButton from '../../../../components/atom/ActionIconButton';
import TablePager from '../../../../components/atom/TablePager';
import { useModals, MODAL_TYPE } from '../../../../hooks/useModal';
import { useNotify } from '../../../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../../../hooks/useSwaggerClient';
import AdminHeader from '../../../../components/atom/AdminHeader';
import { DeleteIcon, EditIcon } from '../../../../icons';

function UserList({ clientId }) {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();
    const [roleToFilter, setRoleToFilter] = useState('all');

    // users, loading, error, setUsers, reloadUsers
    const [users, loading, error, , reloadUsers] = useSwaggerClient(
        'users',
        'listUsersPerClient',
        {
            clientId,
            page,
            pageSize,
            search,
            roleFilter: roleToFilter === 'all' ? undefined : roleToFilter
        },
        [page, pageSize, search, roleToFilter]
    );
    // wire error to a notification
    useEffect(() => {
        if (error) {
            notifyError(error.message);
        }
    }, [error, notifyError]);

    const deleteUser = useCallback(
        userId => {
            doSwaggerCall('users', 'deleteUser', { userId })
                .then(() => {
                    // we finished, remove user from list
                    notifySuccess('User deleted');
                    reloadUsers();
                })
                .catch(() => {
                    notifyError('Cannot delete user');
                });
        },
        [users, notifyError, notifySuccess, reloadUsers]
    );

    if (!users || users.length === 0) {
        return (
            <Grid container justifyContent="center">
                <h1> No users found for this client.</h1>
            </Grid>
        );
    }

    return (
        <Grid container spacing={2}>
            <AdminHeader
                title="Users"
                search={search}
                onSearch={newSearch => {
                    setPage(0);
                    setSearch(newSearch);
                }}
            >
                <Grid item>
                    <TextField
                        select
                        variant="outlined"
                        size="small"
                        label="Role"
                        value={roleToFilter}
                        onChange={e => {
                            setPage(0);
                            setRoleToFilter(e.target.value);
                        }}
                        style={{ minWidth: 120 }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="agent">Agent</MenuItem>
                        <MenuItem value="customer">Customer</MenuItem>
                    </TextField>
                </Grid>
                <AgentListUploader clientId={clientId} reloadUsers={reloadUsers} />
            </AdminHeader>
            <Grid item xs={12}>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Email</TableCell>
                                    <TableCell>First Name</TableCell>
                                    <TableCell>Last Name</TableCell>
                                    <TableCell>Phone Number</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(users?.users ?? []).map(user => (
                                    <TableRow key={user.id}>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.firstname}</TableCell>
                                        <TableCell>{user.lastname}</TableCell>
                                        <TableCell>{formatPhone(user.phone)}</TableCell>
                                        <TableCell>{user.role}</TableCell>
                                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                                            <ActionIconButton
                                                title="Edit"
                                                icon={EditIcon}
                                                to={`/admin/client/${clientId}/users/${user.id}`}
                                            />
                                            <ActionIconButton
                                                title="Delete"
                                                icon={DeleteIcon}
                                                onClick={() => {
                                                    showModal(MODAL_TYPE.CONFIRMATION, {
                                                        title: `Are you sure you want to remove ${user.email}?`,
                                                        content: 'This action is irreversible.',
                                                        confirm: () => deleteUser(user.id)
                                                    });
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <Grid container justifyContent="flex-end">
                    <TablePager
                        total={users?.total}
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}

export default UserList;
